import React, { useState, useEffect } from "react";
import {
  Input,
  Button,
  Typography,
  Checkbox,
  Row,
  Col,
  Card,
  Divider,
  message,
  Form,
  Modal,
} from "antd";
import {
  MailOutlined,
  LockOutlined,
  UserOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { notify } from "../../../utils/helper";
import auth from "../../../services/auth";
import { useHistory, useLocation, Link } from "react-router-dom";
import { extractToken } from "../../../utils/localStorage";
import { headerMenuItems, roleAccess } from "../../../utils/menu-items";
import { LoginCard } from "./Styled";
import AuthenticatorCode from "../components/AuthenticatorCode";
import EmailVerificationConfirmation from "../components/EmailVerificationConfirmation";
import { apiPostWithCustomToken } from "../../../utils/axios";
import { domainUrl } from "../../../utils/domainUrls";
import SubscribeSuccessfulModal from "../../pages/pricing_page/SubscribeSuccessfulModal";
import parseJwt from "../../../utils/parseJwt";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const { Title, Text, Paragraph } = Typography;

const OTPInput = ({ value, onChange, numInputs = 6 }) => {
  const [otp, setOtp] = useState(new Array(numInputs).fill(""));
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const inputRefs = React.useRef([]);

  useEffect(() => {
    onChange(otp.join(""));
  }, [otp, onChange]);

  // Auto-focus first empty input on mount
  useEffect(() => {
    const firstEmptyIndex = otp.findIndex(digit => digit === "");
    if (firstEmptyIndex !== -1 && inputRefs.current[firstEmptyIndex]) {
      inputRefs.current[firstEmptyIndex].focus();
    }
  }, []);

  const handleChange = (element, index) => {
    const value = element.value;
    if (value && isNaN(value)) return false;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input if current input is filled
    if (value && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[index]) {
        // Clear current input if it has value
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous input and clear it if current is empty
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste functionality
  const handlePaste = (e, index) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, ""); // Remove non-digits
    
    if (pasteData.length > 0) {
      const newOtp = [...otp];
      const availableSlots = numInputs - index;
      const digitsToPaste = pasteData.slice(0, availableSlots).split("");
      
      digitsToPaste.forEach((digit, i) => {
        if (index + i < numInputs) {
          newOtp[index + i] = digit;
        }
      });
      
      setOtp(newOtp);
      
      // Focus the next empty input or the last input
      const nextFocusIndex = Math.min(index + digitsToPaste.length, numInputs - 1);
      inputRefs.current[nextFocusIndex]?.focus();
    }
  };

  const getInputStyle = (index) => ({
    width: "50px",
    height: "50px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "600",
    border: `2px solid ${
      focusedIndex === index 
        ? "#3366ff" 
        : otp[index] 
        ? "#52c41a" 
        : "#d9d9d9"
    }`,
    borderRadius: "12px",
    transition: "all 0.2s ease",
    backgroundColor: otp[index] ? "#f6ffed" : "#ffffff",
    boxShadow: focusedIndex === index 
      ? "0 0 0 3px rgba(51, 102, 255, 0.1)" 
      : "none",
  });

  return (
    <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
      {otp.map((data, index) => (
        <Input
          key={index}
          ref={(ref) => (inputRefs.current[index] = ref)}
          type="text"
          maxLength="1"
          style={getInputStyle(index)}
          value={data}
          onChange={(e) => handleChange(e.target, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          onFocus={(e) => {
            e.target.select();
            setFocusedIndex(index);
          }}
          onBlur={() => setFocusedIndex(-1)}
          placeholder="•"
        />
      ))}
    </div>
  );
};

const TwoFactorModal = ({
  visible,
  onCancel,
  qrCode,
  qrToken,
  onVerify,
  loading,
}) => {
  const [otpCode, setOtpCode] = useState("");

  const handleOtpChange = (value) => {
    setOtpCode(value);
  };

  const handleVerify = () => {
    if (otpCode.length === 6) {
      onVerify(otpCode);
    } else {
      message.error("Please enter a valid 6-digit code");
    }
  };

  const handleCancel = () => {
    setOtpCode("");
    onCancel();
  };

  // Clear OTP when modal opens
  useEffect(() => {
    if (visible) {
      setOtpCode("");
    }
  }, [visible]);

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={480}
      closable={false}
      centered
      styles={{
        body: { 
          padding: "20px 16px",
          maxHeight: "85vh",
          overflowY: "auto"
        }
      }}
    >
      <div>
        <Row gutter={[16, 20]} justify="center">
          {/* Header */}
          <Col span={24}>
            <div style={{ textAlign: "center", marginBottom: "8px" }}>
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background:
                    "linear-gradient(135deg, #3366ff 0%, #6600ff 100%)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  boxShadow: "0 6px 24px rgba(51, 102, 255, 0.3)",
                }}
              >
                <SafetyOutlined style={{ fontSize: "28px", color: "white" }} />
              </div>
              <Title
                level={3}
                style={{ 
                  marginBottom: "8px", 
                  color: "#1a1a1a",
                  fontWeight: "600"
                }}
              >
                Set up Two-Factor Authentication
              </Title>
              <Text
                style={{
                  color: "#666666",
                  fontSize: "15px",
                  lineHeight: "1.5",
                }}
              >
                Secure your account with an additional layer of protection
              </Text>
            </div>
          </Col>

          {/* QR Code */}
          <Col span={24}>
            <div
              style={{
                background: "linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)",
                borderRadius: "16px",
                padding: "20px",
                textAlign: "center",
                border: "2px solid #e6f0ff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative elements */}
              <div
                style={{
                  position: "absolute",
                  top: "-15px",
                  right: "-15px",
                  width: "50px",
                  height: "50px",
                  background: "rgba(51, 102, 255, 0.1)",
                  borderRadius: "50%",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-10px",
                  left: "-10px",
                  width: "40px",
                  height: "40px",
                  background: "rgba(102, 0, 255, 0.05)",
                  borderRadius: "50%",
                }}
              />
              
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    display: "inline-block",
                    padding: "12px",
                    backgroundColor: "white",
                    borderRadius: "12px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    marginBottom: "12px",
                  }}
                >
                  <img
                    src={qrCode}
                    alt="QR Code for 2FA setup"
                    style={{
                      width: "160px",
                      height: "160px",
                      borderRadius: "6px",
                      display: "block",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "4px" }}>
                  <Text
                    style={{
                      color: "#1a1a1a",
                      fontSize: "14px",
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "6px",
                    }}
                  >
                    Scan with your authenticator app
                  </Text>
                  <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
                    <Text style={{ color: "#3366ff", fontSize: "12px", fontWeight: "500" }}>
                      Google Authenticator
                    </Text>
                    <Text style={{ color: "#3366ff", fontSize: "12px", fontWeight: "500" }}>
                      Authy
                    </Text>
                    <Text style={{ color: "#3366ff", fontSize: "12px", fontWeight: "500" }}>
                      Microsoft Authenticator
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </Col>

          {/* OTP Input */}
          <Col span={24}>
            <div style={{ textAlign: "center" }}>
              <Text
                strong
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#1a1a1a",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Enter Verification Code
              </Text>
              <Text
                style={{
                  color: "#666666",
                  fontSize: "13px",
                  display: "block",
                  marginBottom: "16px",
                }}
              >
                Enter the 6-digit code from your authenticator app
              </Text>
              
              <OTPInput
                value={otpCode}
                onChange={handleOtpChange}
                numInputs={6}
              />
              
              <div style={{ 
                marginTop: "8px", 
                fontSize: "12px", 
                color: "#999999" 
              }}>
                You can paste your code here
              </div>
            </div>
          </Col>

          {/* Action Buttons */}
          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
              }}
            >
              <Button
                size="large"
                onClick={handleCancel}
                style={{
                  minWidth: "120px",
                  height: "42px",
                  borderRadius: "10px",
                  fontWeight: "500",
                  border: "2px solid #d9d9d9",
                }}
              >
                Cancel
              </Button>
              <Button
                // type="primary"
                size="large"
                loading={loading}
                onClick={handleVerify}
                disabled={otpCode.length !== 6}
                style={{
                  minWidth: "120px",
                  height: "42px",
                  backgroundImage:
                    "linear-gradient(135deg, #3366ff 0%, #6600ff 100%)",
                  border: "none",
                  color: "white",
                  borderRadius: "10px",
                  fontWeight: "700",
                  boxShadow: otpCode.length === 6 
                    ? "0 4px 16px rgba(51, 102, 255, 0.4)" 
                    : "none",
                  transform: otpCode.length === 6 ? "translateY(-1px)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                {loading ? "Verifying..." : "Complete Setup"}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

// Professional Rotating Text Component
const RotatingTextDisplay = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const messages = [
    "Revolutionize Doctor-Patient Communication. Reduce No-Shows, Save Staff Time.",
    "Unlock AI To Improve Patient Satisfaction and Education While Increasing Your Bottom Line.",
    "Convenience. Compliance. Analytics."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % messages.length);
        setIsVisible(true);
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 30px",
        maxWidth: "500px",
        margin: "0 auto",
      }}
    >
      {/* Text Content */}
      <div
        style={{
          textAlign: "center",
          transform: isVisible ? "translateY(0)" : "translateY(10px)",
          opacity: isVisible ? 1 : 0,
          transition: "all 0.3s ease-in-out",
          marginBottom: "20px",
        }}
      >
        <Text
          style={{
            color: "rgba(255, 255, 255, 0.95)",
            fontSize: "18px",
            fontWeight: "400",
            lineHeight: "1.6",
            letterSpacing: "0.5px",
            textAlign: "center",
            display: "block",
            textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
          }}
        >
          {messages[currentIndex]}
        </Text>
      </div>

      {/* Simple Progress Indicators */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
        }}
      >
        {messages.map((_, index) => (
          <div
            key={index}
            style={{
              width: index === currentIndex ? "24px" : "8px",
              height: "3px",
              borderRadius: "2px",
              background: index === currentIndex 
                ? "linear-gradient(135deg, #3366ff 0%, #6600ff 100%)" 
                : "rgba(255, 255, 255, 0.3)",
              transition: "all 0.4s ease",
              opacity: index === currentIndex ? 1 : 0.6,
            }}
          />
        ))}
      </div>
    </div>
  );
};

const MedicalIllustration = () => (
  <div
    className="illustration-container"
    style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      position: "relative",
      padding: "40px 20px",
    }}
  >
    <div
      style={{
        position: "absolute",
        width: "120%",
        height: "120%",
        background:
          "radial-gradient(circle at center, rgba(102, 0, 255, 0.3) 0%, rgba(51, 102, 255, 0.1) 50%, transparent 70%)",
        top: "-10%",
        left: "-10%",
        zIndex: 0,
      }}
    />

    {/* Animated Circles */}
    <div
      className="floating-circle"
      style={{
        position: "absolute",
        width: 300,
        height: 300,
        borderRadius: "50%",
        background:
          "linear-gradient(135deg, rgba(102, 0, 255, 0.2) 0%, rgba(51, 102, 255, 0.1) 100%)",
        top: "20%",
        left: "30%",
        animation: "float 8s ease-in-out infinite",
        zIndex: 1,
      }}
    />

    <div
      className="floating-circle"
      style={{
        position: "absolute",
        width: 200,
        height: 200,
        borderRadius: "50%",
        background:
          "linear-gradient(135deg, rgba(102, 0, 255, 0.1) 0%, rgba(51, 102, 255, 0.05) 100%)",
        bottom: "10%",
        right: "20%",
        animation: "float 6s ease-in-out infinite 1s",
        zIndex: 1,
      }}
    />

    {/* Logo and Brand Section */}
    <div
      style={{
        position: "relative",
        zIndex: 2,
        textAlign: "center",
        marginBottom: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={process.env.PUBLIC_URL + "/MED-stacked-white.png"}
        alt="Meducate Logo and Title"
        style={{
          width: 260,
          height: "auto",
          objectFit: "contain",
          display: "block",
        }}
      />
      <div
        style={{
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)",
          backdropFilter: "blur(10px)",
          borderRadius: "25px",
          padding: "4px 20px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          display: "inline-block",
          marginBottom: "0",
          position: "relative",
          top: "-25px",
        }}
      >
        <span
          style={{
            color: "rgba(255, 255, 255, 0.95)",
            fontSize: "12px",
            fontWeight: 500,
            letterSpacing: "2px",
            textTransform: "uppercase",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
            fontFamily: 'Montserrat, Arial, sans-serif',
          }}
        >
          Care With Clarity
        </span>
      </div>
    </div>

    {/* Rotating Text Section */}
    <div
      style={{
        position: "relative",
        zIndex: 2,
        width: "100%",
        maxWidth: "480px",
      }}
    >
      <RotatingTextDisplay />
    </div>

    {/* Bottom Wave */}
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: "25%",
        background:
          "linear-gradient(to top, rgba(102, 0, 255, 0.2) 0%, transparent 100%)",
        clipPath: "polygon(0 50%, 100% 0, 100% 100%, 0% 100%)",
        zIndex: 0,
      }}
    />
  </div>
);

/**
 * LoginPage Component with reCAPTCHA v3 Integration
 * 
 * This component implements a secure login form with Google reCAPTCHA v3 protection.
 * reCAPTCHA v3 runs in the background and provides a score based on user behavior.
 * 
 * Features:
 * - Secure login with email and password
 * - reCAPTCHA v3 verification on form submission
 * - Error handling for reCAPTCHA failures
 * - Loading states for both form submission and reCAPTCHA verification
 * - Visual reCAPTCHA badge for user awareness
 * - Two-factor authentication support
 * - OTP verification via email or authenticator app
 */
const LoginPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showAuthenticatorModal, setShowAuthenticatorModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [isOTPCodeVerify, setIsOTPCodeVerify] = useState(false);
  const [oTPSentToEmail, setOTPSentToEmail] = useState(false);
  const [form] = Form.useForm();
  const token = extractToken();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const paymentToken = urlParams.get('payment-token');

  useEffect(() => {
    if(paymentToken){
      const details = parseJwt(paymentToken);
      if(details?.success){
        setIsModalOpen(true);
      }
    }
  }, [paymentToken]);

  const handleSubmit = async (values) => {
    setLoading(true);
    setOTPSentToEmail(false)
    try {
      // Execute reCAPTCHA verification
      if (!executeRecaptcha) {
        notify('error', 'reCAPTCHA not available. Please refresh the page and try again.');
        setLoading(false);
        return;
      }

      setRecaptchaLoading(true);
      let recaptchaToken;
      try {
        // Execute reCAPTCHA with 'login' action - this helps Google understand the context
        // and provide appropriate scoring based on the action being performed
        recaptchaToken = await executeRecaptcha('login');
        // console.log('reCAPTCHA token:', recaptchaToken);
        
        if (!recaptchaToken) {
          notify('error', 'reCAPTCHA verification failed. Please try again.');
          setLoading(false);
          setRecaptchaLoading(false);
          return;
        }
      } catch (recaptchaError) {
        // console.error('reCAPTCHA error:', recaptchaError);
        notify('error', 'reCAPTCHA verification failed. Please try again.');
        setLoading(false);
        setRecaptchaLoading(false);
        return;
      } finally {
        setRecaptchaLoading(false);
      }

      const response = await auth.login({
        email: values.email,
        password: values.password,
        reCaptchaToken: recaptchaToken // Add reCAPTCHA token to payload
      });

      // console.log("loginRes:", response);

      if (response.status === 200 || response.status === 201) {
        if (response.data.user.result === "generate") {
          setQrCodeData({
            qrCode: response.data.user.qrCode,
            qrToken: response.data.user.qrToken,
          });
          setShowTwoFactorModal(true);
          setLoading(false);
        }
        if (response.data.user.result === "enable") {
          setShowAuthenticatorModal(true);
          setLoading(false);
          setQrCodeData({
            qrCode: response.data.user.qrCode,
            qrToken: response.data.user.qrToken,
          });
        }

        if (response.data.user.result === "logged") {
          localStorage.setItem(
            "accessToken",
            response?.data?.user?.token
          );
          let user = extractToken();
          // console.log("user:", user);
          setLoading(false);
          if (user?.type) {
            const filteredMenuItems = headerMenuItems.filter(
              (item) => item.visible && roleAccess(user?.role, item.roles)
            );
            
            let defaultPath = filteredMenuItems[0]?.path;
            
            for (const item of filteredMenuItems) {
              if (item.children && item.children.length > 0) {
                const validChild = item.children.find((child) => 
                  roleAccess(user?.role, child.roles)
                );
                if (validChild) {
                  defaultPath = validChild.path;
                  break;
                }
              } else if (item.path && item.path !== "#") {
                defaultPath = item.path;
                break;
              }
            }

            const from = location.state?.from?.pathname || defaultPath;
            // console.log("from:", from);
            // history.replace(from);
            window.location = from
          }
        }
      }
    } catch (error) {
      notify(
        "error",
        error.response?.data?.message || "Failed to login. Please try again."
      );
      setLoading(false);
    }
  };

  const handleTwoFactorCancel = () => {
    setShowTwoFactorModal(false);
    setQrCodeData(null);
  };

  const handleVerifyOTP = async (code) => {
    try {
      setIsOTPCodeVerify(true);
     
       const response = await apiPostWithCustomToken(
        domainUrl("/v1/auth/verify-totp"),
        { totp: code },
        false,
        qrCodeData?.qrToken
      );

      localStorage.setItem(
            "accessToken",
            response?.data?.token
          );
          let user = extractToken();
          setLoading(false);
          if (user?.sub) {
            const filteredMenuItems = headerMenuItems.filter(
              (item) => item.visible && roleAccess(user?.role, item.roles)
            );
            const from =
              location.state?.from?.pathname || filteredMenuItems[0]?.path;
            // history.replace(from);
            window.location = from
          }
      setIsOTPCodeVerify(true);
      notify("success", "OTP Code has been verified successfully.");
    } catch (error) {
      setIsOTPCodeVerify(true);
    }
  };

  const emailCodeSendConfirm = async () => {
    try {
      setIsOTPCodeVerify(true);

      const res = await apiPostWithCustomToken(
        domainUrl("/v1/auth/send-otp"),
        { otpType: "email" },
        false,
        qrCodeData?.qrToken
      );
      setOTPSentToEmail(true);
      setIsOTPCodeVerify(false);
      setShowAlternatives(false);
      setShowAuthenticatorModal(true);
      notify("success", "OTP has been sent to registered E-mail successfully.");
    } catch (error) {
      setIsOTPCodeVerify(false);
      // console.log("error:", error);
    }
  };

  const handleVerifyEmailOTP = async (code) => {
    // console.log("code:", code);
    try {
      setIsOTPCodeVerify(true);

      const response = await apiPostWithCustomToken(
        domainUrl("/v1/auth/verify-otp"),
        { otp: code, otpType: "email" },
        false,
        qrCodeData?.qrToken
      );
      setIsOTPCodeVerify(false);
      // console.log("resss:", response)

      setShowAuthenticatorModal(false);
       localStorage.setItem(
            "accessToken",
            response?.data?.token
          );
          let user = extractToken();
          setLoading(false);
          if (user?.sub) {
            const filteredMenuItems = headerMenuItems.filter(
              (item) => item.visible && roleAccess(user?.role, item.roles)
            );
            const from =
              location.state?.from?.pathname || filteredMenuItems[0]?.path;
            // history.replace(from);
            window.location = from
          }
    } catch (error) {
      setIsOTPCodeVerify(false);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
      }}
    >

    {isModalOpen && (
        <SubscribeSuccessfulModal
          isOpen={isModalOpen}
          setOpen={setIsModalOpen}
        />
      )}

      {showTwoFactorModal && (
        <TwoFactorModal
          visible={showTwoFactorModal}
          onCancel={handleTwoFactorCancel}
          qrCode={qrCodeData.qrCode}
          qrToken={qrCodeData.qrToken}
          onVerify={handleVerifyOTP}
          loading={twoFactorLoading}
        />
      )}

      {showAuthenticatorModal && (
        <AuthenticatorCode
          visible={showAuthenticatorModal}
          setVisible={setShowAuthenticatorModal}
          oTPSentToEmail={oTPSentToEmail}
          onVerify={(code) => {
            if (oTPSentToEmail) {
              handleVerifyEmailOTP(code);
            } else {
              handleVerifyOTP(code);
            }
          }}
          loading={isOTPCodeVerify}
          onTryOtherOptions={() => {
            setShowAuthenticatorModal(false);
            setShowAlternatives(true);
          }}
        />
      )}

      {showAlternatives && (
        <EmailVerificationConfirmation
          visible={showAlternatives}
          setVisible={setShowAlternatives}
          onConfirm={emailCodeSendConfirm}
          loading={isOTPCodeVerify}
          setShowAuthenticatorModal={setShowAuthenticatorModal}
        />
      )}

      {/* Left side - (40%) */}
      <div
        style={{
          flex: "0 0 40%",
          background: "linear-gradient(135deg, #111122 0%, #1a1a40 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <MedicalIllustration />
      </div>

      {/* Right side - (60%) */}
      <div
        style={{
          flex: "0 0 60%",
          background: "white",
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px",
        }}
      >
        <div style={{ maxWidth: 450, width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <Title level={2} style={{ fontSize: 32, marginBottom: 8 }}>
              Welcome Back
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Sign in to continue to Meducate
            </Text>
          </div>

          <LoginCard
            bordered={false}
            style={{
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              borderRadius: 12,
            }}
            bodyStyle={{ padding: 32 }}
          >
            <Form
              form={form}
              onFinish={handleSubmit}
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  prefix={
                    <MailOutlined style={{ color: "rgba(0, 0, 0, 0.25)" }} />
                  }
                  placeholder="Enter your email"
                  size="large"
                  style={{
                    height: 50,
                    borderRadius: 8,
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                  { min: 8, message: "Password must be at least 8 characters" },
                ]}
              >
                <Input.Password
                  prefix={
                    <LockOutlined style={{ color: "rgba(0, 0, 0, 0.25)" }} />
                  }
                  placeholder="Enter your password"
                  size="large"
                  style={{
                    height: 50,
                    borderRadius: 8,
                  }}
                />
              </Form.Item>

              <Form.Item>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 32,
                    alignItems: "center",
                  }}
                >
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox>Remember me</Checkbox>
                  </Form.Item>
                  <Link to="/forget-password" style={{ color: "#3366ff" }}>
                    Forgot password?
                  </Link>
                </div>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={loading || recaptchaLoading}
                  htmlType="submit"
                  style={{
                    height: 50,
                    backgroundImage:
                      "linear-gradient(135deg, #3366ff 0%, #6600ff 100%)",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 500,
                    marginBottom: 24,
                  }}
                >
                  {recaptchaLoading ? 'Verifying...' : 'Sign In'}
                </Button>
              </Form.Item>
            </Form>
          </LoginCard>

          <div style={{ textAlign: "center", marginTop: 24 }}>
            <Text type="secondary">
              Don't have an account?{" "}
              <Link to="/pricing" style={{ color: "#3366ff", fontWeight: 500 }}>
                Sign Up
              </Link>
            </Text>
          </div>
          
          {/* reCAPTCHA Badge */}
          <div className="recaptcha-badge">
            <div className="recaptcha-badge-icon">
              ✓
            </div>
            <span>Protected by reCAPTCHA</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
          100% {
            transform: translateY(0px);
          }
        }

        @keyframes logoFloat {
          0% {
            transform: rotate(45deg) translateY(0px);
          }
          50% {
            transform: rotate(45deg) translateY(-10px);
          }
          100% {
            transform: rotate(45deg) translateY(0px);
          }
        }

        .illustration-container {
          animation-name: fadeIn;
          animation-duration: 1s;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .recaptcha-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 16px;
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.04);
          border-radius: 6px;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.45);
        }

        .recaptcha-badge-icon {
          width: 16px;
          height: 16px;
          background: #52c41a;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 10px;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;